import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Gift, Clock, Sparkles, X } from 'lucide-react';
import { createPortal } from 'react-dom';

const DailyRewardCountdown = ({ 
  secondsRemaining = 0, 
  available = false,
  onClick,
  compact = false 
}) => {
  const [timeLeft, setTimeLeft] = useState(secondsRemaining);
  const [showTooltip, setShowTooltip] = useState(false);
  
  // Update countdown every second
  useEffect(() => {
    setTimeLeft(secondsRemaining);
    
    if (secondsRemaining <= 0) return;
    
    const interval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(interval);
  }, [secondsRemaining]);
  
  // Format time as HH:MM:SS
  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleClick = () => {
    if (available || timeLeft <= 0) {
      onClick?.();
    } else {
      setShowTooltip(true);
    }
  };
  
  // COMPACT ICON - Always shows just a Gift icon with notification badge
  return (
    <>
      <motion.button
        onClick={handleClick}
        className={`relative p-2 sm:p-2.5 rounded-xl transition-all ${
          available || timeLeft <= 0
            ? 'bg-gradient-to-br from-green-500 to-emerald-600 shadow-lg shadow-green-500/40'
            : 'bg-gray-800/80 hover:bg-gray-700/80'
        }`}
        animate={available || timeLeft <= 0 ? { 
          scale: [1, 1.05, 1],
        } : {}}
        transition={{ duration: 1.5, repeat: Infinity }}
        whileTap={{ scale: 0.95 }}
        data-testid="daily-reward-btn"
        title={available ? "Claim Daily Bonus!" : `Next bonus in ${formatTime(timeLeft)}`}
      >
        <Gift className={`w-5 h-5 sm:w-6 sm:h-6 ${available || timeLeft <= 0 ? 'text-white' : 'text-gray-400'}`} />
        
        {/* Red Notification Badge when available */}
        {(available || timeLeft <= 0) && (
          <motion.div
            className="absolute -top-1 -right-1 w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-red-500 border-2 border-black flex items-center justify-center"
            animate={{ scale: [1, 1.3, 1] }}
            transition={{ duration: 0.8, repeat: Infinity }}
          >
            <span className="text-[8px] text-white font-bold">!</span>
          </motion.div>
        )}
      </motion.button>

      {/* Timer Tooltip - Shows on click when not available */}
      <AnimatePresence>
        {showTooltip && !available && timeLeft > 0 && createPortal(
          <motion.div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowTooltip(false)}
          >
            <motion.div
              className="bg-[#0d0d0d] border-2 border-gray-600 rounded-2xl p-6 max-w-xs w-full text-center"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={e => e.stopPropagation()}
            >
              <button 
                onClick={() => setShowTooltip(false)}
                className="absolute top-3 right-3 p-1 hover:bg-white/10 rounded-lg"
              >
                <X className="w-4 h-4 text-white/60" />
              </button>
              
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gray-800 flex items-center justify-center">
                <Clock className="w-8 h-8 text-gray-400" />
              </div>
              
              <h3 className="text-white font-bold text-lg mb-2">Daily Bonus</h3>
              <p className="text-white/60 text-sm mb-4">Come back later to claim!</p>
              
              <div className="bg-black/50 rounded-xl px-4 py-3 border border-white/10">
                <span className="text-xs text-white/40 uppercase tracking-wider">Next Reward In</span>
                <div className="font-mono text-2xl text-yellow-400 font-bold mt-1">
                  {formatTime(timeLeft)}
                </div>
              </div>
            </motion.div>
          </motion.div>,
          document.body
        )}
      </AnimatePresence>
    </>
  );
};

export default DailyRewardCountdown;
